import React, { useState } from "react"
import Label from "@kit/design/label"
import { ColorInput } from "@kit/design/color-picker"
import Stack from "@kit/design/stack"
import { Expander } from "app/regions/blocks/shared/expander"
import { BorderSize } from "../../section/utils/border-size"
import { OptionButton } from "../../section/utils/option-button"
import { CustomInput } from "../../section/utils/custom-input"
import { getInitialOption } from "./get-initial-option"
import { useSavedColors } from "hooks/use-saved-colors"

const RADIUS_DEFAULTS = {
  none: 0,
  normal: 4,
  large: 12
}

function BorderRadius({ properties, update }) {
  const [radiusOption, setRadiusOption] = useState(
    getInitialOption(
      properties,
      [
        "borderTopLeftRadius",
        "borderTopRightRadius",
        "borderBottomLeftRadius",
        "borderBottomRightRadius"
      ],
      RADIUS_DEFAULTS
    )
  )

  function handleChange(option, value) {
    if (option !== "custom") {
      setRadiusOption(option)
    }

    const newProperties =
      option === "custom"
        ? value
        : {
            borderTopRightRadius: RADIUS_DEFAULTS[option],
            borderTopLeftRadius: RADIUS_DEFAULTS[option],
            borderBottomRightRadius: RADIUS_DEFAULTS[option],
            borderBottomLeftRadius: RADIUS_DEFAULTS[option]
          }
    update(newProperties, {
      localStoragePrefix: `layout-${properties.template}`
    })
  }

  return (
    <Stack spacing={2}>
      <div className="font-semibold text-gray-700">Radius</div>
      <div className="flex border rounded">
        <div className="p-1 flex items-center justify-center flex-grow border-r">
          <OptionButton
            active={radiusOption === "none"}
            onClick={() => handleChange("none")}
          >
            None
          </OptionButton>
          <OptionButton
            active={radiusOption === "normal"}
            onClick={() => handleChange("normal")}
          >
            Normal
          </OptionButton>
          <OptionButton
            active={radiusOption === "large"}
            onClick={() => handleChange("large")}
          >
            Large
          </OptionButton>
        </div>
        <div className="p-1 flex items-center justify-center">
          <OptionButton
            active={radiusOption === "custom"}
            onClick={() => setRadiusOption("custom")}
          >
            Custom
          </OptionButton>
        </div>
      </div>

      {radiusOption === "custom" && (
        <div className="grid grid-cols-2 gap-2">
          <CustomInput
            label="Top left radius"
            icon="top-left-radius"
            value={properties.borderTopLeftRadius}
            onChange={value =>
              handleChange("custom", { borderTopLeftRadius: value })
            }
          />
          <CustomInput
            label="Top right radius"
            icon="top-right-radius"
            value={properties.borderTopRightRadius}
            onChange={value =>
              handleChange("custom", { borderTopRightRadius: value })
            }
          />
          <CustomInput
            label="Bottom left radius"
            icon="bottom-left-radius"
            value={properties.borderBottomLeftRadius}
            onChange={value =>
              handleChange("custom", { borderBottomLeftRadius: value })
            }
          />
          <CustomInput
            label="Bottom right radius"
            icon="bottom-right-radius"
            value={properties.borderBottomRightRadius}
            onChange={value =>
              handleChange("custom", { borderBottomRightRadius: value })
            }
          />
        </div>
      )}
    </Stack>
  )
}

function BorderColor({ properties, update }) {
  const savedColors = useSavedColors()

  return (
    <div>
      <Label htmlFor="borderColor">Color</Label>
      <ColorInput
        id="borderColor"
        name="borderColor"
        onChange={color =>
          update(
            { borderColor: color.hex },
            { localStoragePrefix: `layout-${properties.template}` }
          )
        }
        value={properties.borderColor}
        savedColors={savedColors}
      />
    </div>
  )
}

export function BorderSettings({ properties, update }) {
  return (
    <Expander label="Border">
      <BorderColor properties={properties} update={update} />
      <BorderSize properties={properties} update={update} />
      <BorderRadius properties={properties} update={update} />
    </Expander>
  )
}
